import { useState, useCallback, useMemo } from "react";
import { useSnackbar } from "notistack";
import { backendGet, backendPost, backendDelete } from "services/dataService.js";

export function usePaymentMatchingData(paymentImportId) {
  const { enqueueSnackbar } = useSnackbar();

  const [paymentImport, setPaymentImport] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentMatchings, setPaymentMatchings] = useState([]);
  const [bookings, setBookings] = useState([]);

  const [loadingPaymentImport, setLoadingPaymentImport] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingMatchings, setLoadingMatchings] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const bookingIn = useMemo(
    () => bookings.filter((booking) => booking.type === "IN"),
    [bookings]
  );

  const bookingOut = useMemo(
    () => bookings.filter((booking) => booking.type === "OUT"),
    [bookings]
  );

  const differenceAmount = useMemo(() => {
    if (!bookings || bookings.length === 0) return null;

    const sum = bookings.reduce((acc, b) => {
      const value = parseFloat(String(b.amount).replace(",", ".") || 0);
      return acc + (b.type === "OUT" ? -value : value);
    }, 0);

    return sum.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
    });
  }, [bookings]);

  const fetchPaymentImport = useCallback(async () => {
    if (!paymentImportId) return;

    try {
      await backendGet(`data/payment-imports/${paymentImportId}`, {
        label: "PaymentImport",
        setIsLoading: setLoadingPaymentImport,
        enqueueSnackbar,
        setResult: (res) => {
          setPaymentImport(res.data);
        },
      });
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden des Zahlungsimports.", {
        variant: "error",
      });
    }
  }, [paymentImportId, enqueueSnackbar]);

  const fetchPayments = useCallback(async () => {
    if (!paymentImportId) return;

    try {
      await backendGet(`data/payment-imports/${paymentImportId}/payments`, {
        label: "Payments",
        setIsLoading: setLoadingPayments,
        options: {
          params: {
            include: ["booking"],
          },
        },
        enqueueSnackbar,
        setResult: (res) => {
          const filtered = res.data.filter(
            (payment) => payment.status !== "failed"
          );
          setPayments(filtered);
        },
      });
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden der Zahlungen.", {
        variant: "error",
      });
    }
  }, [paymentImportId, enqueueSnackbar]);

  const fetchPaymentMatchings = useCallback(async () => {
    if (!paymentImportId) return;

    try {
      await backendGet("data/payment-matchings", {
        label: "PaymentMatching",
        setIsLoading: setLoadingMatchings,
        enqueueSnackbar,
        setResult: (res) => {
          setPaymentMatchings(res.data);
        },
        options: {
          params: {
            payment_import_id: paymentImportId,
            is_assigned: false,
            include: ["payment", "schaden"],
          },
        },
      });
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden der Zahlungen.", {
        variant: "error",
      });
    }
  }, [paymentImportId, enqueueSnackbar]);

  const fetchBookings = useCallback(
    async (claimId) => {
      if (!claimId) return;

      try {
        await backendGet(`data/schadens/${claimId}/bookings`, {
          label: "Bookings",
          setIsLoading: setLoadingBookings,
          options: {
            params: {
              api_version: "v2",
            },
          },
          enqueueSnackbar,
          setResult: (res) => {
            setBookings(res.data || []);
          },
        });
      } catch (error) {
        enqueueSnackbar("Fehler beim Laden der Zahlungen.", {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar]
  );

  const fetchAllInitialData = useCallback(async () => {
    await Promise.all([
      fetchPaymentImport(),
      fetchPayments(),
      fetchPaymentMatchings(),
    ]);
  }, [fetchPaymentImport, fetchPayments, fetchPaymentMatchings]);

  const deleteBooking = useCallback(
    async (bookingId, { setIsLoading } = {}) => {
      try {
        await backendDelete(`data/bookings/${bookingId}`, {
          label: "Zuordnung löschen",
          enqueueSnackbar,
          setIsLoading,
        });

        setPayments((prev) =>
          prev.map((payment) => {
            if (payment.booking_id === bookingId) {
              return {
                ...payment,
                booking_id: null,
              };
            }
            return payment;
          })
        );

        setBookings((prev) =>
          prev.filter((booking) => booking.id !== bookingId)
        );

        return true;
      } catch (error) {
        enqueueSnackbar("Fehler beim Löschen der Zuordnung.", {
          variant: "error",
        });
        return false;
      }
    },
    [enqueueSnackbar]
  );

  const assignPaymentsToClaim = useCallback(
    async (claimId, paymentIds, { setIsLoading, createRefund = false } = {}) => {
      if (!claimId || paymentIds.length === 0) return null;

      const response = await backendPost("data/payments/assign-to-schaden", {
        label: "Zahlungszuordnung",
        setIsLoading: setIsLoading || setLoadingMatchings,
        toSend: {
          schaden_id: claimId,
          payment_ids: paymentIds,
          create_refund: createRefund,
        },
        enqueueSnackbar,
      });

      const { bookings: createdBookings, payments: updatedPayments } = response;

      setBookings((prev) => [...(prev || []), ...(createdBookings || [])]);

      setPayments((prev) => [
        ...prev.filter((p) => !paymentIds.includes(p.id)),
        ...(updatedPayments || []),
      ]);

      // Refresh payment matchings
      await fetchPaymentMatchings();

      return response;
    },
    [enqueueSnackbar, fetchPaymentMatchings]
  );

  const approveAllExactMatches = useCallback(
    async ({ setIsLoading, onSuccess } = {}) => {
      if (!paymentImport?.id) return;

      try {
        await backendPost("data/payment-matchings/approve-exacts", {
          setIsLoading,
          label: "Alle exakten Schäden zuordnen",
          toSend: { payment_import_id: paymentImport.id },
          enqueueSnackbar,
          setResult: () => {
            enqueueSnackbar(
              "Alle exakten Schäden wurden erfolgreich zugeordnet.",
              {
                variant: "success",
              }
            );

            fetchPaymentMatchings();
            fetchPayments();
            onSuccess?.();
          },
        });
      } catch (error) {
        enqueueSnackbar("Fehler beim Zuordnen der exakten Schäden.", {
          variant: "error",
        });
      }
    },
    [paymentImport?.id, enqueueSnackbar, fetchPaymentMatchings, fetchPayments]
  );

  return {
    // State
    paymentImport,
    payments,
    paymentMatchings,
    bookings,
    bookingIn,
    bookingOut,
    differenceAmount,

    // Loading states
    loadingPaymentImport,
    loadingPayments,
    loadingMatchings,
    loadingBookings,

    // Fetch functions
    fetchPaymentImport,
    fetchPayments,
    fetchPaymentMatchings,
    fetchBookings,
    fetchAllInitialData,

    // Actions
    deleteBooking,
    assignPaymentsToClaim,
    approveAllExactMatches,

    // State setters (for external updates)
    setPayments,
    setBookings,
    setPaymentMatchings,
  };
}
